import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {BasketsPartialState} from './baskets.reducer';
import {BasketsQuery} from './baskets.selectors';
import * as BasketsActions from './baskets.actions';
import {ActionWithCorrelationAttributes} from '@kion/ngrx-helper';
import {DeliveryMethod, PaymentInfo} from '@kion/data-models';

@Injectable()
export class BasketsFacade {

  all$ = this.store.pipe(select(BasketsQuery.getAllBaskets));
  allById$ = this.store.pipe(select(BasketsQuery.getAllBasketsById));
  searchResults$ = this.store.pipe(select(BasketsQuery.getSearchResults));
  isSearching$ = this.store.pipe(select(BasketsQuery.getIsSearching));
  lastCreatedId$ = this.store.pipe(select(BasketsQuery.getLastCreatedId));
  deliveryMethods$ = this.store.pipe(select(BasketsQuery.getDeliveryMethods));
  payments$ = this.store.pipe(select(BasketsQuery.getPayments));
  supportedPayments$ = this.store.pipe(select(BasketsQuery.getSupportedPayments));
  order$ = this.store.pipe(select(BasketsQuery.getOrder));
  redirectUrl$ = this.store.pipe(select(BasketsQuery.getRedirectUrl));
  error$ = this.store.pipe(select(BasketsQuery.getError));
  step$ = this.store.pipe(select(BasketsQuery.getStep));

  constructor(private store: Store<BasketsPartialState>) { }

  protected dispatch(action, correlationId?: string): ActionWithCorrelationAttributes {
    if (correlationId) {
      action.__correlationId = correlationId;
    }
    this.store.dispatch(action);
    return action;
  }

  createOrFindBasket( basketId?: string, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.createOrFindBasket({  basketId: basketId, userId: userId }),
      correlationId
    );
  }

  addToBasket( basketId: string, productId: string, qty: number, options?: {code: string}[], userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.addToBasket({  basketId: basketId, productId: productId, qty: qty, options: options, userId: userId }),
      correlationId
    );
  }
  addToBasketWithUnit( basketId: string, productId: string, qty: number, unitIso?:string, options?: {code: string}[], userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.addToBasketWithUnit({  basketId: basketId, productId: productId, qty: qty, unitIso: unitIso, options: options, userId: userId }),
      correlationId
    );
  }

  loadDeliveryMethods( basketId?: string, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.loadDeliveryDataByBasket({  basketId: basketId, userId: userId }),
      correlationId
    );
  }
  setDeliveryMethod( basketId: string, deliveryMethod: DeliveryMethod, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.setDeliveryMethod({

        basketId: basketId,
        deliveryMethod: deliveryMethod,
        userId: userId
      }),
      correlationId
    );
  }
  loadById( basketId: string, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.loadById({  basketId: basketId, userId: userId }),
      correlationId
    );
  }
  addEmailToBasket( basketId: string, email: string, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.addEmailToBasket({  basketId: basketId, email: email, userId: userId }),
      correlationId
    );
  }
  loadPayments( correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.loadPaymentMethods(),
      correlationId
    );
  }
  loadSupportedPayments( correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.loadSupportedPaymentMethods(),
      correlationId
    );
  }
  setPayment( basketId: string, payment: Partial<PaymentInfo>, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.setPaymentInfo({   basketId: basketId, payment: payment, userId: userId}),
      correlationId
    )
  }
  placeOrder( basketId: string, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.placeOrder({  basketId: basketId, userId: userId }),
      correlationId
    );
  }
  finalizePayment(basketId: string, transactionId: string, userId?: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.finalizePayment({ basketId: basketId, transactionId: transactionId, userId: userId }),
      correlationId
    );
  }
  changeQuantity(basketId: string, entryNumber: string, quantity: number, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.changeQuantity({ basketId: basketId, entryNumber: entryNumber, quantity: quantity }),
      correlationId
    );
  }
  deleteEntry(basketId: string, entryNumber: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.deleteEntry({ basketId: basketId, entryNumber: entryNumber }),
      correlationId
    );
  }
  transformAnonymousToPersonal( userId: string, oldBasketId: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.transformAnonymousToPersonal({ userId, oldBasketId }),
      correlationId
    );
  }
  addUserReference( basketId: string, reference: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.addUserReference({ basketId: basketId,  userReference: reference}),
      correlationId
    );
  }
  setAddress(basketId: string, addressId: string, isDelivery: boolean, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.setAddress({ basketId: basketId, addressId: addressId, isDeliveryAddress: isDelivery }),
      correlationId
    );
  }
  addPromotion(basketId: string, promotionCode: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.addPromotionCode({ basketId: basketId, promotionCode: promotionCode }),
      correlationId
    );
  }
  deletePromotion(basketId: string, promotionCode: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.deletePromotionCode({ basketId: basketId, promotionCode: promotionCode }),
      correlationId
    );
  }
  checkStep(correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.checkStep(),
      correlationId
    )
  }

  updatePurchaseOrderNumber(basketId: string, purchaseOrderNumber: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.updatePurchaseOrderNumber({basketId: basketId, purchaseOrderNumber: purchaseOrderNumber}),
      correlationId
    );
  }

  applyPostalCodeMapping(basketId: string, correlationId?: string): ActionWithCorrelationAttributes {
    return this.dispatch(
      BasketsActions.applyPostalCodeMapping({basketId}),
      correlationId
    )
  }
  
  setCompleteDelivery(basketId: string, completeDelivery: boolean, correlationId?: string): ActionWithCorrelationAttributes {
      return this.dispatch(
        BasketsActions.setCompleteDelivery({basketId, completeDelivery}),
        correlationId
      )
  }
}
